import type { LangMap } from "./index";

/** Area professori: profilo docente, classi virtuali, bacheca. */
const base: LangMap = {
  it: {
    role: {
      question: "Come userai Lumen?",
      subtitle: "Puoi cambiare in qualsiasi momento dal profilo.",
      student: "Studente",
      studentDesc: "Studio, tutor AI, mappe, quiz e planner.",
      teacher: "Professore",
      teacherDesc: "Classi virtuali, contenuti e progressi della classe.",
      parent: "Genitore",
      parentDesc: "Segui i progressi di tuo figlio, in sola lettura.",
      saved: "Ruolo aggiornato",
    },
    profile: {
      title: "Profilo professore",
      subtitle: "Solo i campi utili: il resto è facoltativo.",
      name: "Nome",
      subjects: "Materie insegnate",
      subjectsHint: "Separate da virgola",
      levels: "Livello scolastico",
      school: "Scuola (facoltativo)",
      years: "Anni di esperienza (facoltativo)",
      languages: "Lingue usate",
      classTypes: "Tipologia di classi",
      bio: "Presentazione (facoltativo)",
      isPublic: "Profilo visibile agli altri docenti",
      save: "Salva profilo",
      saved: "Profilo salvato",
    },
    dash: {
      title: "Area professore",
      subtitle: "Le tue classi e i tuoi strumenti didattici.",
      classes: "Le mie classi",
      create: "Attività da creare",
      assigned: "Attività assegnate",
      results: "Risultati",
      contents: "Contenuti",
      community: "Community docenti",
      assistant: "AI Teacher Assistant",
      soon: "In arrivo nella prossima fase",
      empty: "Non hai ancora classi. Creane una per iniziare.",
    },
    classes: {
      new: "Nuova classe",
      newTitle: "Crea una classe virtuale",
      name: "Nome classe",
      namePh: "3A — Matematica",
      subject: "Materia",
      level: "Livello",
      year: "Anno scolastico",
      description: "Descrizione (facoltativa)",
      create: "Crea classe",
      created: "Classe creata",
      members: "{{count}} studenti",
      code: "Codice di accesso",
      copyCode: "Copia codice",
      copied: "Codice copiato",
      copyLink: "Copia link di invito",
      rotate: "Rigenera codice",
      rotated: "Nuovo codice generato",
      inviteOpen: "Iscrizioni aperte",
      leaderboard: "Classifica di classe (facoltativa)",
      leaderboardHint: "Disattivata per impostazione predefinita: nessun confronto pubblico tra studenti.",
      archive: "Archivia classe",
      open: "Apri classe",
      settings: "Impostazioni",
      studentsTitle: "Studenti iscritti",
      studentsEmpty: "Nessuno studente iscritto: condividi il codice.",
      joined: "Iscritto il {{date}}",
      progressTitle: "Andamento studenti",
      progressEmpty: "Nessun dato: assegna un'attività per vedere i progressi.",
      progressSubmitted: "{{done}}/{{total}} consegnate",
      progressMissing: "{{count}} mancanti",
      progressAvg: "Media",
      progressNoAvg: "Non valutato",
      progressLast: "Ultima attività {{date}}",
      progressNever: "Nessuna attività",
      progressRefresh: "Aggiorna",
    },
    join: {
      title: "Entra in una classe",
      subtitle: "Inserisci il codice che ti ha dato il professore.",
      code: "Codice classe",
      submit: "Entra",
      joined: "Sei entrato in {{name}}",
      notFound: "Codice non valido",
      closed: "Le iscrizioni a questa classe sono chiuse",
      ownClass: "Questa è una tua classe",
      myClasses: "Le mie classi",
      leave: "Esci dalla classe",
      left: "Hai lasciato la classe",
    },
    board: {
      title: "Bacheca",
      write: "Scrivi una comunicazione",
      publish: "Pubblica",
      ask: "Chiedi al professore",
      askPh: "Non ho capito questo passaggio...",
      anonymous: "Invia in modo anonimo alla classe",
      send: "Invia domanda",
      sent: "Domanda inviata",
      answer: "Rispondi",
      answered: "Risposta del professore",
      empty: "Ancora nessun messaggio.",
      question: "Domanda",
      announcement: "Comunicazione",
      anonymousBadge: "Anonima",
      delete: "Elimina",
      needDate: "Scegli data e ora di invio",
      preview: "Anteprima prima di pubblicare",
      previewTitle: "Anteprima avviso",
      scheduleAt: "Invia il",
      publishNow: "Pubblica ora",
      publishedNow: "Avviso pubblicato",
      mode: { published: "Pubblica subito", draft: "Bozza", scheduled: "Programma" },
      submit: { published: "Pubblica", draft: "Salva bozza", scheduled: "Programma invio" },
    },
    tabs: {
      board: "Discussioni",
      syllabus: "Programma",
      materials: "Materiali",
      work: "Attività",
      calendar: "Calendario",
      challenges: "Sfide",
      students: "Studenti",
      progress: "Progressi",
      settings: "Impostazioni",
    },
    status: { draft: "Bozza", review: "In revisione", published: "Pubblicato" },
    syllabus: {
      addTitle: "Aggiungi un argomento al programma",
      titlePh: "Le equazioni di secondo grado",
      descPh: "Cosa affronterete in questa lezione…",
      date: "Data prevista",
      add: "Aggiungi al programma",
      added: "Argomento aggiunto",
      delete: "Elimina",
      empty: "Il programma non è ancora stato compilato.",
      progress: "Avanzamento del programma",
      status: { planned: "Da fare", in_progress: "In corso", done: "Svolto" },
    },
    materials: {
      empty: "Nessun materiale condiviso con la classe.",
      preview: "Anteprima",
      hide: "Nascondi",
      publish: "Pubblica in classe",
      published: "Materiale pubblicato",
    },
    calendar: {
      empty: "Nessun impegno in calendario.",
      kind: { lesson: "Lezione", assignment: "Consegna", challenge: "Sfida" },
    },
    privacy: {
      note: "Privacy: i voti restano visibili solo a te e allo studente, le classifiche sono disattivate e le domande possono essere anonime.",
    },
    studio: {
      title: "Crea contenuti",
      subtitle: "Verifiche, esercizi e spiegazioni pronte in un minuto.",
      kind: "Tipo di contenuto",
      kinds: {
        test: "Verifica",
        exercise: "Esercizi",
        explanation: "Spiegazione",
        summary: "Riassunto",
        map: "Mappa/scaletta",
        rubric: "Griglia di valutazione",
        flashcards: "Flashcard",
        video: "Video",
        podcast: "Podcast",
        presentation: "Presentazione",
        lesson: "Piano di lezione",
        challenge: "Sfida",
      },
      topic: "Argomento",
      topicPh: "Le equazioni di secondo grado",
      subject: "Materia",
      level: "Livello classe",
      difficulty: "Difficoltà",
      easy: "Facile",
      medium: "Media",
      hard: "Difficile",
      count: "Numero di domande/esercizi",
      minutes: "Durata prevista (minuti)",
      notes: "Note o materiale di partenza (facoltativo)",
      inclusive: "Aggiungi variante inclusiva (DSA/ADHD/BES)",
      generate: "Genera con l'AI",
      generating: "Sto preparando il contenuto...",
      generated: "Contenuto creato",
      library: "I miei materiali",
      empty: "Nessun materiale creato per ora.",
      delete: "Elimina",
      assign: "Assegna alla classe",
      preview: "Anteprima",
      answerKey: "Soluzioni",
      questions: "Domande",
      rubric: "Griglia",
      points: "{{n}} punti",
      goal: "Obiettivo",
      cards: "Flashcard",
      slides: "Slide",
      script: "Copione",
      steps: "Passaggi",
      needed: "Serve",
      status: "Stato",
    },
    assign: {
      title: "Assegna alla classe",
      chooseClass: "Classe",
      title_: "Titolo",
      instructions: "Istruzioni per gli studenti",
      due: "Consegna entro",
      maxScore: "Punteggio massimo",
      confirm: "Assegna",
      done: "Attività assegnata",
      kind: "Tipo",
      homework: "Compito",
      test: "Verifica",
      practice: "Esercitazione",
      list: "Attività della classe",
      empty: "Nessuna attività assegnata.",
      submitted: "{{n}} consegne",
      graded: "{{n}} valutate",
      avg: "Media {{v}}",
      openResults: "Vedi consegne",
      noDue: "Senza scadenza",
    },
    work: {
      title: "Compiti e verifiche",
      subtitle: "Le attività assegnate dai tuoi professori.",
      empty: "Non hai attività assegnate.",
      due: "Entro il {{date}}",
      open: "Apri",
      yourAnswer: "La tua risposta",
      submit: "Invia consegna",
      submitted: "Consegnato",
      resubmit: "Aggiorna consegna",
      sent: "Consegna inviata",
      score: "Voto: {{score}}/{{max}}",
      feedback: "Commento del professore",
      pending: "In attesa di valutazione",
      notEnrolled: "Non sei iscritto a questa classe: chiedi al docente il codice di accesso prima di consegnare.",
    },
    grade: {
      title: "Consegne",
      empty: "Nessuna consegna ricevuta.",
      score: "Voto",
      feedback: "Commento",
      save: "Salva valutazione",
      saved: "Valutazione salvata",
      analyze: "Analisi AI dei risultati",
      analyzing: "Sto analizzando...",
      stats: "{{count}} valutate · media {{avg}} · min {{min}} · max {{max}}",
      noData: "Servono almeno una valutazione per l'analisi.",
      answer: "Risposta",
    },
    challenge: {
      title: "Sfide di classe",
      empty: "Nessuna sfida attiva.",
      new: "Nuova sfida",
      name: "Titolo della sfida",
      namePh: "Maratona di ripasso",
      description: "Descrizione",
      metric: "Obiettivo",
      metrics: {
        minutes: "Minuti di studio",
        tasks: "Attività completate",
        quiz: "Quiz svolti",
        points: "Punti",
      },
      target: "Traguardo",
      ends: "Fine",
      reward: "XP in premio",
      create: "Crea sfida",
      created: "Sfida creata",
      close: "Chiudi sfida",
      remove: "Elimina",
      closed: "Chiusa",
      leaderboard: "Classifica",
      noPlayers: "Nessun partecipante per ora.",
      progress: "{{done}}/{{target}}",
      add: "Aggiungi progresso",
      added: "Progresso registrato",
      completedBadge: "Completata",
    },
    share: {
      title: "Community docenti",
      subtitle: "Cerca materiali condivisi dai colleghi e riusali con la tua classe.",
      search: "Cerca per titolo o argomento",
      filterSubject: "Materia",
      filterKind: "Tipo",
      any: "Tutti",
      empty: "Nessun materiale condiviso trovato.",
      copy: "Copia nella mia libreria",
      copied: "Materiale copiato",
      by: "di {{name}}",
      copies: "{{n}} copie",
      makePublic: "Condividi con i docenti",
      public: "Condiviso",
      private: "Privato",
      sort: "Ordina",
      sorts: { recent: "Recenti", popular: "Popolari", rated: "Migliori" },
      onlySaved: "Salvati",
      onlyFollowing: "Che seguo",
      save: "Salva",
      saved: "Salvato",
      follow: "Segui",
      following: "Segui già",
      reviews_one: "{{count}} recensione",
      reviews_other: "{{count}} recensioni",
      reviews_zero: "Recensioni",
      rateIt: "Dai una valutazione",
      yourReview: "La tua recensione",
      reviewPh: "Come è andata in classe? Cosa cambieresti?",
      sendReview: "Pubblica",
      deleteReview: "Elimina",
      reviewSaved: "Recensione salvata",
      noReviews: "Ancora nessuna recensione: puoi essere il primo.",
      starLabel: "{{n}} stelle",
      stats: { shared: "Condivisi", copies: "Riusi", rating: "Voto medio", followers: "Follower" },
    },
    parentArea: {
      linkStudent: "Username dello studente",
      linkPh: "@nomeutente",
      send: "Invia richiesta",
      sent: "Richiesta inviata",
      notFound: "Studente non trovato",
      children: "Studenti collegati",
      requests: "Richieste ricevute",
      accept: "Accetta",
      decline: "Rifiuta",
      revoke: "Revoca",
      stats: "Livello {{level}} · {{xp}} XP · streak {{streak}}",
      noRequests: "Nessuna richiesta.",
    },
    results: {
      title: "Risultati della classe",
      subtitle: "Consegne, valutazioni e medie delle attività assegnate.",
      activities: "Attività",
      submissions: "Consegne",
      average: "Media",
      notGraded: "Da valutare",
      empty: "Nessuna attività assegnata a questa classe.",
      openClass: "Apri la classe per valutare",
      kind: "Tipo",
      due: "Scadenza",
      gradedCol: "Valutate",
      maxScore: "Punteggio max",
      percent: "% media",
      exportCsv: "Esporta CSV",
      exportXlsx: "Esporta Excel",
      exportPdf: "Stampa / PDF",
      exported: "File scaricato",
      popupBlocked: "Consenti i popup per stampare",
    },
    notify: {
      title: "Avvisi delle classi",
      hint: "Scegli quanto spesso ricevere gli avvisi dei docenti.",
      push: "Notifica push per i nuovi avvisi",
      freq: { instant: "Subito", daily: "Ogni giorno", off: "Mai" },
    },
    audit: {
      title: "Registro comunicazioni",
      subtitle: "Chi ha pubblicato, ricevuto e letto gli avvisi della classe.",
      event: {
        announcement_created: "ha pubblicato un avviso",
        announcement_updated: "ha modificato un avviso",
        announcement_deleted: "ha eliminato un avviso",
        announcement_delivered: "ha ricevuto un avviso",
        announcement_read: "ha aperto un avviso",
      },
      filters: "Filtri e ricerca",
      searchPh: "Cerca per nome o evento",
      allEvents: "Tutti",
      from: "Dal",
      to: "Al",
      empty: "Nessun evento con questi filtri.",
    },
    engagement: {
      title: "Engagement degli avvisi",
      subtitle: "Percentuale di letture, tempo medio di apertura e destinatari più attivi.",
      readRate: "{{pct}}% letto · {{n}}/{{tot}}",
      avgTime: "Tempo medio di lettura: {{v}}",
      topReaders: "Destinatari più attivi",
      reads: "{{n}} letture",
    },
    studentHome: {
      title: "Compiti dalle tue classi",
      sub_one: "{{count}} attività da consegnare",
      sub_other: "{{count}} attività da consegnare",
      all: "Vedi tutte le classi",
      today: "Oggi",
      noDue: "Senza scadenza",
      newsTitle: "Avvisi dalle tue classi",
      newsSub_one: "{{count}} nuovo avviso",
      newsSub_other: "{{count}} nuovi avvisi",
    },
    parent: {
      title: "Area genitore",
      subtitle: "Progressi di tuo figlio, in sola lettura e solo con il suo consenso.",
      link: "Collega uno studente",
      linkHint: "Serve il consenso dello studente dal suo profilo.",
      pending: "In attesa di consenso",
      accepted: "Collegato",
      empty: "Nessuno studente collegato.",
    },
  },
  en: {
    role: {
      question: "How will you use Lumen?",
      subtitle: "You can change this any time from your profile.",
      student: "Student",
      studentDesc: "Study, AI tutor, maps, quizzes and planner.",
      teacher: "Teacher",
      teacherDesc: "Virtual classes, content and class progress.",
      parent: "Parent",
      parentDesc: "Follow your child's progress, read-only.",
      saved: "Role updated",
    },
    profile: {
      title: "Teacher profile",
      subtitle: "Only what's useful: everything else is optional.",
      name: "Name",
      subjects: "Subjects taught",
      subjectsHint: "Comma separated",
      levels: "School level",
      school: "School (optional)",
      years: "Years of experience (optional)",
      languages: "Languages used",
      classTypes: "Class types",
      bio: "About you (optional)",
      isPublic: "Visible to other teachers",
      save: "Save profile",
      saved: "Profile saved",
    },
    dash: {
      title: "Teacher area",
      subtitle: "Your classes and teaching tools.",
      classes: "My classes",
      create: "Content to create",
      assigned: "Assigned activities",
      results: "Results",
      contents: "Content",
      community: "Teacher community",
      assistant: "AI Teacher Assistant",
      soon: "Coming in the next phase",
      empty: "No classes yet. Create one to get started.",
    },
    classes: {
      new: "New class",
      newTitle: "Create a virtual class",
      name: "Class name",
      namePh: "3A — Maths",
      subject: "Subject",
      level: "Level",
      year: "School year",
      description: "Description (optional)",
      create: "Create class",
      created: "Class created",
      members: "{{count}} students",
      code: "Join code",
      copyCode: "Copy code",
      copied: "Code copied",
      copyLink: "Copy invite link",
      rotate: "Regenerate code",
      rotated: "New code generated",
      inviteOpen: "Enrolment open",
      leaderboard: "Class leaderboard (optional)",
      leaderboardHint: "Off by default: no public comparison between students.",
      archive: "Archive class",
      open: "Open class",
      settings: "Settings",
      studentsTitle: "Enrolled students",
      studentsEmpty: "No students yet: share the code.",
      joined: "Joined on {{date}}",
      progressTitle: "Student progress",
      progressEmpty: "No data yet: assign an activity to see progress.",
      progressSubmitted: "{{done}}/{{total}} handed in",
      progressMissing: "{{count}} missing",
      progressAvg: "Average",
      progressNoAvg: "Not graded",
      progressLast: "Last activity {{date}}",
      progressNever: "No activity yet",
      progressRefresh: "Refresh",
    },
    join: {
      title: "Join a class",
      subtitle: "Enter the code your teacher gave you.",
      code: "Class code",
      submit: "Join",
      joined: "You joined {{name}}",
      notFound: "Invalid code",
      closed: "Enrolment for this class is closed",
      ownClass: "This is your own class",
      myClasses: "My classes",
      leave: "Leave class",
      left: "You left the class",
    },
    board: {
      title: "Board",
      write: "Write an announcement",
      publish: "Publish",
      ask: "Ask the teacher",
      askPh: "I didn't understand this step...",
      anonymous: "Send anonymously to the class",
      send: "Send question",
      sent: "Question sent",
      answer: "Answer",
      answered: "Teacher's answer",
      empty: "No messages yet.",
      question: "Question",
      announcement: "Announcement",
      anonymousBadge: "Anonymous",
      delete: "Delete",
      needDate: "Pick a send date and time",
      preview: "Preview before publishing",
      previewTitle: "Announcement preview",
      scheduleAt: "Send on",
      publishNow: "Publish now",
      publishedNow: "Announcement published",
      mode: { published: "Publish now", draft: "Draft", scheduled: "Schedule" },
      submit: { published: "Publish", draft: "Save draft", scheduled: "Schedule send" },
    },
    tabs: {
      board: "Discussions",
      syllabus: "Syllabus",
      materials: "Materials",
      work: "Activities",
      calendar: "Calendar",
      challenges: "Challenges",
      students: "Students",
      progress: "Progress",
      settings: "Settings",
    },
    status: { draft: "Draft", review: "In review", published: "Published" },
    syllabus: {
      addTitle: "Add a topic to the syllabus",
      titlePh: "Quadratic equations",
      descPh: "What this lesson covers…",
      date: "Planned date",
      add: "Add to syllabus",
      added: "Topic added",
      delete: "Delete",
      empty: "The syllabus is empty for now.",
      progress: "Syllabus progress",
      status: { planned: "Planned", in_progress: "In progress", done: "Done" },
    },
    materials: {
      empty: "No material shared with this class yet.",
      preview: "Preview",
      hide: "Hide",
      publish: "Publish to class",
      published: "Material published",
    },
    calendar: {
      empty: "Nothing scheduled yet.",
      kind: { lesson: "Lesson", assignment: "Due", challenge: "Challenge" },
    },
    privacy: {
      note: "Privacy: grades stay visible only to you and the student, leaderboards are off, and questions can be anonymous.",
    },
    studio: {
      title: "Create content",
      subtitle: "Tests, exercises and explanations ready in a minute.",
      kind: "Content type",
      kinds: {
        test: "Test",
        exercise: "Exercises",
        explanation: "Explanation",
        summary: "Summary",
        map: "Map/outline",
        rubric: "Marking rubric",
        flashcards: "Flashcards",
        video: "Video",
        podcast: "Podcast",
        presentation: "Presentation",
        lesson: "Lesson plan",
        challenge: "Challenge",
      },
      topic: "Topic",
      topicPh: "Quadratic equations",
      subject: "Subject",
      level: "Class level",
      difficulty: "Difficulty",
      easy: "Easy",
      medium: "Medium",
      hard: "Hard",
      count: "Number of questions/exercises",
      minutes: "Expected duration (minutes)",
      notes: "Notes or source material (optional)",
      inclusive: "Add inclusive variant (SEN/ADHD/dyslexia)",
      generate: "Generate with AI",
      generating: "Preparing the content...",
      generated: "Content created",
      library: "My materials",
      empty: "No materials yet.",
      delete: "Delete",
      assign: "Assign to class",
      preview: "Preview",
      answerKey: "Answer key",
      questions: "Questions",
      rubric: "Rubric",
      points: "{{n}} points",
      goal: "Goal",
      cards: "Flashcards",
      slides: "Slides",
      script: "Script",
      steps: "Steps",
      needed: "You need",
      status: "Status",
    },
    assign: {
      title: "Assign to class",
      chooseClass: "Class",
      title_: "Title",
      instructions: "Instructions for students",
      due: "Due by",
      maxScore: "Maximum score",
      confirm: "Assign",
      done: "Activity assigned",
      kind: "Type",
      homework: "Homework",
      test: "Test",
      practice: "Practice",
      list: "Class activities",
      empty: "No activities assigned.",
      submitted: "{{n}} submissions",
      graded: "{{n}} graded",
      avg: "Average {{v}}",
      openResults: "View submissions",
      noDue: "No deadline",
    },
    work: {
      title: "Homework and tests",
      subtitle: "Activities assigned by your teachers.",
      empty: "You have no assigned activities.",
      due: "Due {{date}}",
      open: "Open",
      yourAnswer: "Your answer",
      submit: "Submit",
      submitted: "Submitted",
      resubmit: "Update submission",
      sent: "Submission sent",
      score: "Score: {{score}}/{{max}}",
      feedback: "Teacher feedback",
      pending: "Awaiting grading",
      notEnrolled: "You're not enrolled in this class: ask your teacher for the join code before submitting.",
    },
    grade: {
      title: "Submissions",
      empty: "No submissions yet.",
      score: "Score",
      feedback: "Feedback",
      save: "Save grade",
      saved: "Grade saved",
      analyze: "AI results analysis",
      analyzing: "Analysing...",
      stats: "{{count}} graded · average {{avg}} · min {{min}} · max {{max}}",
      noData: "At least one grade is needed for the analysis.",
      answer: "Answer",
    },
    challenge: {
      title: "Class challenges",
      empty: "No active challenges.",
      new: "New challenge",
      name: "Challenge title",
      namePh: "Revision marathon",
      description: "Description",
      metric: "Goal",
      metrics: {
        minutes: "Study minutes",
        tasks: "Completed activities",
        quiz: "Quizzes taken",
        points: "Points",
      },
      target: "Target",
      ends: "Ends",
      reward: "XP reward",
      create: "Create challenge",
      created: "Challenge created",
      close: "Close challenge",
      remove: "Delete",
      closed: "Closed",
      leaderboard: "Leaderboard",
      noPlayers: "No participants yet.",
      progress: "{{done}}/{{target}}",
      add: "Add progress",
      added: "Progress saved",
      completedBadge: "Completed",
    },
    share: {
      title: "Teacher community",
      subtitle: "Find materials shared by colleagues and reuse them with your class.",
      search: "Search by title or topic",
      filterSubject: "Subject",
      filterKind: "Type",
      any: "All",
      empty: "No shared materials found.",
      copy: "Copy to my library",
      copied: "Material copied",
      by: "by {{name}}",
      copies: "{{n}} copies",
      makePublic: "Share with teachers",
      public: "Shared",
      private: "Private",
      sort: "Sort",
      sorts: { recent: "Recent", popular: "Popular", rated: "Top rated" },
      onlySaved: "Saved",
      onlyFollowing: "Following",
      save: "Save",
      saved: "Saved",
      follow: "Follow",
      following: "Following",
      reviews_one: "{{count}} review",
      reviews_other: "{{count}} reviews",
      reviews_zero: "Reviews",
      rateIt: "Rate this material",
      yourReview: "Your review",
      reviewPh: "How did it go in class? What would you change?",
      sendReview: "Post",
      deleteReview: "Delete",
      reviewSaved: "Review saved",
      noReviews: "No reviews yet — be the first.",
      starLabel: "{{n}} stars",
      stats: { shared: "Shared", copies: "Reuses", rating: "Avg rating", followers: "Followers" },
    },
    parentArea: {
      linkStudent: "Student username",
      linkPh: "@username",
      send: "Send request",
      sent: "Request sent",
      notFound: "Student not found",
      children: "Linked students",
      requests: "Incoming requests",
      accept: "Accept",
      decline: "Decline",
      revoke: "Revoke",
      stats: "Level {{level}} · {{xp}} XP · streak {{streak}}",
      noRequests: "No requests.",
    },
    results: {
      title: "Class results",
      subtitle: "Submissions, grades and averages for your assigned activities.",
      activities: "Activities",
      submissions: "Submissions",
      average: "Average",
      notGraded: "Not graded",
      empty: "No activities assigned to this class yet.",
      openClass: "Open the class to grade",
      kind: "Type",
      due: "Due date",
      gradedCol: "Graded",
      maxScore: "Max score",
      percent: "Avg %",
      exportCsv: "Export CSV",
      exportXlsx: "Export Excel",
      exportPdf: "Print / PDF",
      exported: "File downloaded",
      popupBlocked: "Allow pop-ups to print",
    },
    notify: {
      title: "Class updates",
      hint: "Choose how often you get teacher announcements.",
      push: "Push notification for new updates",
      freq: { instant: "Instantly", daily: "Daily", off: "Never" },
    },
    audit: {
      title: "Communications log",
      subtitle: "Who published, received and opened the class announcements.",
      event: {
        announcement_created: "published an announcement",
        announcement_updated: "edited an announcement",
        announcement_deleted: "deleted an announcement",
        announcement_delivered: "received an announcement",
        announcement_read: "opened an announcement",
      },
      filters: "Filters and search",
      searchPh: "Search by name or event",
      allEvents: "All",
      from: "From",
      to: "To",
      empty: "No events match these filters.",
    },
    engagement: {
      title: "Announcement engagement",
      subtitle: "Read rate, average time to open and most active recipients.",
      readRate: "{{pct}}% read · {{n}}/{{tot}}",
      avgTime: "Average read time: {{v}}",
      topReaders: "Most active recipients",
      reads: "{{n}} reads",
    },
    studentHome: {
      title: "Homework from your classes",
      sub_one: "{{count}} task to hand in",
      sub_other: "{{count}} tasks to hand in",
      all: "See all classes",
      today: "Today",
      noDue: "No due date",
      newsTitle: "Updates from your classes",
      newsSub_one: "{{count}} new update",
      newsSub_other: "{{count}} new updates",
    },
    parent: {
      title: "Parent area",
      subtitle: "Your child's progress, read-only and only with their consent.",
      link: "Link a student",
      linkHint: "The student approves the link from their profile.",
      pending: "Waiting for consent",
      accepted: "Linked",
      empty: "No linked students.",
    },
  },
};

const EN = base["en"] as Record<string, any>;

/** Traduzioni area docenti/famiglia: le chiavi non tradotte ricadono sull'inglese. */
const es: Record<string, unknown> = {
  ...EN,
  role: { ...EN["role"], question: "¿Cómo usarás Lumen?", subtitle: "Puedes cambiarlo cuando quieras desde tu perfil.", student: "Estudiante", studentDesc: "Estudio, tutor IA, mapas, cuestionarios y planificador.", teacher: "Profesor/a", teacherDesc: "Clases virtuales, contenidos y progreso de la clase.", parent: "Familia", parentDesc: "Sigue el progreso de tu hijo/a, solo lectura.", saved: "Rol actualizado" },
  profile: { ...EN["profile"], title: "Perfil docente", subtitle: "Solo lo útil: el resto es opcional.", name: "Nombre", subjects: "Asignaturas que impartes", subjectsHint: "Separadas por comas", levels: "Nivel escolar", school: "Centro (opcional)", years: "Años de experiencia (opcional)", languages: "Idiomas", classTypes: "Tipos de clase", bio: "Sobre ti (opcional)", isPublic: "Visible para otros docentes", save: "Guardar perfil", saved: "Perfil guardado" },
  dash: { ...EN["dash"], title: "Área docente", subtitle: "Tus clases y herramientas didácticas.", classes: "Mis clases", create: "Contenido para crear", assigned: "Actividades asignadas", results: "Resultados", contents: "Contenidos", community: "Comunidad docente", assistant: "Asistente IA para docentes", empty: "Aún no hay clases. Crea una para empezar." },
  classes: { ...EN["classes"], new: "Nueva clase", newTitle: "Crear una clase virtual", name: "Nombre de la clase", subject: "Asignatura", level: "Nivel", year: "Curso escolar", description: "Descripción (opcional)", create: "Crear clase", created: "Clase creada", members: "{{count}} estudiantes", code: "Código de acceso", copyCode: "Copiar código", copied: "Código copiado", copyLink: "Copiar enlace de invitación", rotate: "Regenerar código", rotated: "Nuevo código generado", inviteOpen: "Inscripciones abiertas", leaderboard: "Clasificación de clase (opcional)", archive: "Archivar clase", open: "Abrir clase", settings: "Ajustes", studentsTitle: "Estudiantes inscritos", studentsEmpty: "Aún no hay estudiantes: comparte el código.", progressTitle: "Progreso del alumnado", progressAvg: "Media", progressRefresh: "Actualizar" },
  join: { ...EN["join"], title: "Unirse a una clase", subtitle: "Introduce el código que te ha dado tu profe.", code: "Código de clase", submit: "Entrar", joined: "Te has unido a {{name}}", notFound: "Código no válido", myClasses: "Mis clases", leave: "Salir de la clase", left: "Has salido de la clase" },
  board: { ...EN["board"], title: "Tablón", write: "Escribe un aviso", publish: "Publicar", ask: "Pregunta al profe", send: "Enviar pregunta", sent: "Pregunta enviada", answer: "Responder", answered: "Respuesta del profe", empty: "Aún no hay mensajes.", question: "Pregunta", announcement: "Aviso", anonymousBadge: "Anónimo", delete: "Eliminar" },
  tabs: { ...EN["tabs"], board: "Tablón", work: "Actividades", challenges: "Retos", students: "Estudiantes", settings: "Ajustes" },
  studio: { ...EN["studio"], title: "Crear contenido", subtitle: "Exámenes, ejercicios y explicaciones en un minuto.", kind: "Tipo de contenido", topic: "Tema", subject: "Asignatura", difficulty: "Dificultad", easy: "Fácil", medium: "Media", hard: "Difícil", generate: "Generar con IA", generating: "Preparando el contenido...", generated: "Contenido creado", library: "Mis materiales", empty: "Aún no hay materiales.", assign: "Asignar a la clase", preview: "Vista previa" },
  assign: { ...EN["assign"], title: "Asignar a la clase", chooseClass: "Clase", instructions: "Instrucciones para el alumnado", due: "Entrega antes del", maxScore: "Puntuación máxima", confirm: "Asignar", done: "Actividad asignada", list: "Actividades de la clase", empty: "No hay actividades asignadas." },
  work: { ...EN["work"], title: "Deberes y exámenes", subtitle: "Actividades asignadas por tus profes.", empty: "No tienes actividades asignadas.", submit: "Entregar", submitted: "Entregado", sent: "Entrega enviada", feedback: "Comentario del profe", pending: "Pendiente de corrección" },
  grade: { ...EN["grade"], title: "Entregas", empty: "Aún no hay entregas.", score: "Nota", feedback: "Comentario", save: "Guardar nota", saved: "Nota guardada", analyze: "Análisis IA de resultados" },
  challenge: { ...EN["challenge"], title: "Retos de clase", empty: "No hay retos activos.", new: "Nuevo reto", create: "Crear reto", created: "Reto creado", leaderboard: "Clasificación", target: "Objetivo", reward: "Recompensa XP" },
  share: { ...EN["share"], title: "Comunidad docente", subtitle: "Encuentra materiales de otros docentes y reutilízalos.", search: "Buscar por título o tema", copy: "Copiar a mi biblioteca", copied: "Material copiado" , sorts: { recent: "Recientes", popular: "Populares", rated: "Mejor valorados" }, onlySaved: "Guardados", onlyFollowing: "Que sigo", save: "Guardar", saved: "Guardado", follow: "Seguir", following: "Siguiendo", rateIt: "Valora este material", yourReview: "Tu reseña", reviewPh: "¿Cómo fue en clase? ¿Qué cambiarías?", sendReview: "Publicar", deleteReview: "Eliminar", reviewSaved: "Reseña guardada", noReviews: "Aún no hay reseñas: sé el primero.", reviews_one: "{{count}} reseña", reviews_other: "{{count}} reseñas", reviews_zero: "Reseñas", stats: { shared: "Compartidos", copies: "Reutilizaciones", rating: "Nota media", followers: "Seguidores" } },
  parentArea: { ...EN["parentArea"], linkStudent: "Usuario del estudiante", send: "Enviar solicitud", sent: "Solicitud enviada", notFound: "Estudiante no encontrado", children: "Estudiantes vinculados", requests: "Solicitudes recibidas", accept: "Aceptar", decline: "Rechazar", revoke: "Revocar", noRequests: "Sin solicitudes." },
  results: { ...EN["results"], title: "Resultados de la clase", subtitle: "Entregas, notas y medias de tus actividades.", activities: "Actividades", submissions: "Entregas", average: "Media", exportCsv: "Exportar CSV", exportPdf: "Imprimir / PDF", exported: "Archivo descargado" },
  notify: { ...EN["notify"], title: "Novedades de clase", hint: "Elige con qué frecuencia recibir los avisos del profe.", push: "Notificación de nuevos avisos", freq: { instant: "Al instante", daily: "Cada día", off: "Nunca" } },
  audit: { ...EN["audit"], title: "Registro de comunicaciones", subtitle: "Quién ha publicado, recibido y abierto los avisos.", event: { announcement_created: "ha publicado un aviso", announcement_updated: "ha editado un aviso", announcement_deleted: "ha eliminado un aviso", announcement_delivered: "ha recibido un aviso", announcement_read: "ha abierto un aviso" } },
  studentHome: { ...EN["studentHome"], title: "Deberes de tus clases", sub_one: "{{count}} tarea por entregar", sub_other: "{{count}} tareas por entregar", all: "Ver todas las clases", today: "Hoy", noDue: "Sin fecha", newsTitle: "Novedades de tus clases", newsSub_one: "{{count}} novedad", newsSub_other: "{{count}} novedades" },
  parent: { ...EN["parent"], title: "Área familia", subtitle: "El progreso de tu hijo/a, solo lectura y con su consentimiento.", link: "Vincular un estudiante", linkHint: "El estudiante aprueba el vínculo desde su perfil.", pending: "Esperando consentimiento", accepted: "Vinculado", empty: "No hay estudiantes vinculados." },
};

const fr: Record<string, unknown> = {
  ...EN,
  role: { ...EN["role"], question: "Comment vas-tu utiliser Lumen ?", subtitle: "Tu peux changer à tout moment depuis ton profil.", student: "Élève", studentDesc: "Révisions, tuteur IA, cartes, quiz et planning.", teacher: "Enseignant·e", teacherDesc: "Classes virtuelles, contenus et progression.", parent: "Parent", parentDesc: "Suivez la progression de votre enfant, en lecture seule.", saved: "Rôle mis à jour" },
  profile: { ...EN["profile"], title: "Profil enseignant", subtitle: "Seulement l'utile : le reste est facultatif.", name: "Nom", subjects: "Matières enseignées", subjectsHint: "Séparées par des virgules", levels: "Niveau scolaire", school: "Établissement (facultatif)", years: "Années d'expérience (facultatif)", languages: "Langues", classTypes: "Types de cours", bio: "À propos (facultatif)", isPublic: "Visible par les autres enseignants", save: "Enregistrer le profil", saved: "Profil enregistré" },
  dash: { ...EN["dash"], title: "Espace enseignant", subtitle: "Vos classes et vos outils pédagogiques.", classes: "Mes classes", create: "Contenus à créer", assigned: "Activités assignées", results: "Résultats", contents: "Contenus", community: "Communauté enseignante", assistant: "Assistant IA enseignant", empty: "Aucune classe : créez-en une pour commencer." },
  classes: { ...EN["classes"], new: "Nouvelle classe", newTitle: "Créer une classe virtuelle", name: "Nom de la classe", subject: "Matière", level: "Niveau", year: "Année scolaire", description: "Description (facultatif)", create: "Créer la classe", created: "Classe créée", members: "{{count}} élèves", code: "Code d'accès", copyCode: "Copier le code", copied: "Code copié", copyLink: "Copier le lien d'invitation", rotate: "Régénérer le code", rotated: "Nouveau code généré", inviteOpen: "Inscriptions ouvertes", leaderboard: "Classement de classe (facultatif)", archive: "Archiver la classe", open: "Ouvrir la classe", settings: "Paramètres", studentsTitle: "Élèves inscrits", studentsEmpty: "Aucun élève : partagez le code.", progressTitle: "Progression des élèves", progressAvg: "Moyenne", progressRefresh: "Actualiser" },
  join: { ...EN["join"], title: "Rejoindre une classe", subtitle: "Saisis le code donné par ton prof.", code: "Code de classe", submit: "Rejoindre", joined: "Tu as rejoint {{name}}", notFound: "Code invalide", myClasses: "Mes classes", leave: "Quitter la classe", left: "Tu as quitté la classe" },
  board: { ...EN["board"], title: "Mur", write: "Écrire une annonce", publish: "Publier", ask: "Poser une question", send: "Envoyer la question", sent: "Question envoyée", answer: "Répondre", answered: "Réponse du prof", empty: "Aucun message.", question: "Question", announcement: "Annonce", anonymousBadge: "Anonyme", delete: "Supprimer" },
  tabs: { ...EN["tabs"], board: "Mur", work: "Activités", challenges: "Défis", students: "Élèves", settings: "Paramètres" },
  studio: { ...EN["studio"], title: "Créer un contenu", subtitle: "Contrôles, exercices et explications en une minute.", kind: "Type de contenu", topic: "Sujet", subject: "Matière", difficulty: "Difficulté", easy: "Facile", medium: "Moyenne", hard: "Difficile", generate: "Générer avec l'IA", generating: "Préparation du contenu...", generated: "Contenu créé", library: "Mes ressources", empty: "Aucune ressource.", assign: "Assigner à la classe", preview: "Aperçu" },
  assign: { ...EN["assign"], title: "Assigner à la classe", chooseClass: "Classe", instructions: "Consignes pour les élèves", due: "À rendre avant le", maxScore: "Note maximale", confirm: "Assigner", done: "Activité assignée", list: "Activités de la classe", empty: "Aucune activité assignée." },
  work: { ...EN["work"], title: "Devoirs et contrôles", subtitle: "Activités assignées par tes profs.", empty: "Aucune activité assignée.", submit: "Rendre", submitted: "Rendu", sent: "Devoir envoyé", feedback: "Retour du prof", pending: "En attente de correction" },
  grade: { ...EN["grade"], title: "Copies rendues", empty: "Aucun rendu.", score: "Note", feedback: "Commentaire", save: "Enregistrer la note", saved: "Note enregistrée", analyze: "Analyse IA des résultats" },
  challenge: { ...EN["challenge"], title: "Défis de classe", empty: "Aucun défi actif.", new: "Nouveau défi", create: "Créer le défi", created: "Défi créé", leaderboard: "Classement", target: "Objectif", reward: "Récompense XP" },
  share: { ...EN["share"], title: "Communauté enseignante", subtitle: "Trouvez des ressources partagées par vos collègues.", search: "Rechercher par titre ou sujet", copy: "Copier dans ma bibliothèque", copied: "Ressource copiée" , sorts: { recent: "Récents", popular: "Populaires", rated: "Mieux notés" }, onlySaved: "Enregistrés", onlyFollowing: "Que je suis", save: "Enregistrer", saved: "Enregistré", follow: "Suivre", following: "Suivi", rateIt: "Notez cette ressource", yourReview: "Votre avis", reviewPh: "Comment cela s’est passé en classe ?", sendReview: "Publier", deleteReview: "Supprimer", reviewSaved: "Avis enregistré", noReviews: "Aucun avis pour l’instant : soyez le premier.", reviews_one: "{{count}} avis", reviews_other: "{{count}} avis", reviews_zero: "Avis", stats: { shared: "Partagés", copies: "Réutilisations", rating: "Note moyenne", followers: "Abonnés" } },
  parentArea: { ...EN["parentArea"], linkStudent: "Identifiant de l'élève", send: "Envoyer la demande", sent: "Demande envoyée", notFound: "Élève introuvable", children: "Élèves liés", requests: "Demandes reçues", accept: "Accepter", decline: "Refuser", revoke: "Révoquer", noRequests: "Aucune demande." },
  results: { ...EN["results"], title: "Résultats de la classe", subtitle: "Rendus, notes et moyennes de vos activités.", activities: "Activités", submissions: "Rendus", average: "Moyenne", exportCsv: "Exporter en CSV", exportPdf: "Imprimer / PDF", exported: "Fichier téléchargé" },
  notify: { ...EN["notify"], title: "Actualités de classe", hint: "Choisissez la fréquence des annonces du prof.", push: "Notification des nouvelles annonces", freq: { instant: "Immédiat", daily: "Chaque jour", off: "Jamais" } },
  audit: { ...EN["audit"], title: "Journal des communications", subtitle: "Qui a publié, reçu et ouvert les annonces.", event: { announcement_created: "a publié une annonce", announcement_updated: "a modifié une annonce", announcement_deleted: "a supprimé une annonce", announcement_delivered: "a reçu une annonce", announcement_read: "a ouvert une annonce" } },
  studentHome: { ...EN["studentHome"], title: "Devoirs de tes classes", sub_one: "{{count}} devoir à rendre", sub_other: "{{count}} devoirs à rendre", all: "Voir toutes les classes", today: "Aujourd'hui", noDue: "Sans échéance", newsTitle: "Actus de tes classes", newsSub_one: "{{count}} nouveauté", newsSub_other: "{{count}} nouveautés" },
  parent: { ...EN["parent"], title: "Espace parent", subtitle: "La progression de votre enfant, en lecture seule et avec son accord.", link: "Lier un élève", linkHint: "L'élève valide le lien depuis son profil.", pending: "En attente d'accord", accepted: "Lié", empty: "Aucun élève lié." },
};

const de: Record<string, unknown> = {
  ...EN,
  role: { ...EN["role"], question: "Wie möchtest du Lumen nutzen?", subtitle: "Du kannst das jederzeit im Profil ändern.", student: "Schüler:in", studentDesc: "Lernen, KI-Tutor, Maps, Quiz und Planer.", teacher: "Lehrkraft", teacherDesc: "Virtuelle Klassen, Inhalte und Lernfortschritt.", parent: "Eltern", parentDesc: "Den Fortschritt des Kindes verfolgen, nur lesen.", saved: "Rolle aktualisiert" },
  profile: { ...EN["profile"], title: "Lehrkraft-Profil", subtitle: "Nur das Nötige – der Rest ist optional.", name: "Name", subjects: "Unterrichtsfächer", subjectsHint: "Mit Komma trennen", levels: "Schulstufe", school: "Schule (optional)", years: "Berufsjahre (optional)", languages: "Sprachen", classTypes: "Unterrichtsarten", bio: "Über dich (optional)", isPublic: "Für andere Lehrkräfte sichtbar", save: "Profil speichern", saved: "Profil gespeichert" },
  dash: { ...EN["dash"], title: "Lehrkraft-Bereich", subtitle: "Deine Klassen und Werkzeuge.", classes: "Meine Klassen", create: "Inhalte erstellen", assigned: "Zugewiesene Aufgaben", results: "Ergebnisse", contents: "Inhalte", community: "Lehrkräfte-Community", assistant: "KI-Assistent für Lehrkräfte", empty: "Noch keine Klasse. Erstelle eine, um zu starten." },
  classes: { ...EN["classes"], new: "Neue Klasse", newTitle: "Virtuelle Klasse erstellen", name: "Klassenname", subject: "Fach", level: "Stufe", year: "Schuljahr", description: "Beschreibung (optional)", create: "Klasse erstellen", created: "Klasse erstellt", members: "{{count}} Schüler:innen", code: "Beitrittscode", copyCode: "Code kopieren", copied: "Code kopiert", copyLink: "Einladungslink kopieren", rotate: "Code neu erzeugen", rotated: "Neuer Code erzeugt", inviteOpen: "Anmeldung offen", leaderboard: "Klassen-Rangliste (optional)", archive: "Klasse archivieren", open: "Klasse öffnen", settings: "Einstellungen", studentsTitle: "Angemeldete Schüler:innen", studentsEmpty: "Noch niemand dabei – teile den Code.", progressTitle: "Lernfortschritt", progressAvg: "Durchschnitt", progressRefresh: "Aktualisieren" },
  join: { ...EN["join"], title: "Klasse beitreten", subtitle: "Gib den Code deiner Lehrkraft ein.", code: "Klassencode", submit: "Beitreten", joined: "Du bist {{name}} beigetreten", notFound: "Ungültiger Code", myClasses: "Meine Klassen", leave: "Klasse verlassen", left: "Du hast die Klasse verlassen" },
  board: { ...EN["board"], title: "Pinnwand", write: "Mitteilung schreiben", publish: "Veröffentlichen", ask: "Lehrkraft fragen", send: "Frage senden", sent: "Frage gesendet", answer: "Antworten", answered: "Antwort der Lehrkraft", empty: "Noch keine Nachrichten.", question: "Frage", announcement: "Mitteilung", anonymousBadge: "Anonym", delete: "Löschen" },
  tabs: { ...EN["tabs"], board: "Pinnwand", work: "Aufgaben", challenges: "Challenges", students: "Schüler:innen", settings: "Einstellungen" },
  studio: { ...EN["studio"], title: "Inhalt erstellen", subtitle: "Tests, Übungen und Erklärungen in einer Minute.", kind: "Inhaltstyp", topic: "Thema", subject: "Fach", difficulty: "Schwierigkeit", easy: "Leicht", medium: "Mittel", hard: "Schwer", generate: "Mit KI erstellen", generating: "Inhalt wird vorbereitet...", generated: "Inhalt erstellt", library: "Meine Materialien", empty: "Noch keine Materialien.", assign: "Der Klasse zuweisen", preview: "Vorschau" },
  assign: { ...EN["assign"], title: "Der Klasse zuweisen", chooseClass: "Klasse", instructions: "Anweisungen für die Klasse", due: "Abgabe bis", maxScore: "Maximale Punktzahl", confirm: "Zuweisen", done: "Aufgabe zugewiesen", list: "Aufgaben der Klasse", empty: "Keine Aufgaben zugewiesen." },
  work: { ...EN["work"], title: "Hausaufgaben und Tests", subtitle: "Von deinen Lehrkräften zugewiesen.", empty: "Keine Aufgaben für dich.", submit: "Abgeben", submitted: "Abgegeben", sent: "Abgabe gesendet", feedback: "Feedback der Lehrkraft", pending: "Wartet auf Bewertung" },
  grade: { ...EN["grade"], title: "Abgaben", empty: "Noch keine Abgaben.", score: "Note", feedback: "Feedback", save: "Note speichern", saved: "Note gespeichert", analyze: "KI-Analyse der Ergebnisse" },
  challenge: { ...EN["challenge"], title: "Klassen-Challenges", empty: "Keine aktiven Challenges.", new: "Neue Challenge", create: "Challenge erstellen", created: "Challenge erstellt", leaderboard: "Rangliste", target: "Ziel", reward: "XP-Belohnung" },
  share: { ...EN["share"], title: "Lehrkräfte-Community", subtitle: "Materialien von Kolleg:innen finden und wiederverwenden.", search: "Nach Titel oder Thema suchen", copy: "In meine Bibliothek kopieren", copied: "Material kopiert" , sorts: { recent: "Neueste", popular: "Beliebt", rated: "Bestbewertet" }, onlySaved: "Gespeichert", onlyFollowing: "Gefolgt", save: "Speichern", saved: "Gespeichert", follow: "Folgen", following: "Folge ich", rateIt: "Material bewerten", yourReview: "Deine Bewertung", reviewPh: "Wie lief es im Unterricht? Was würdest du ändern?", sendReview: "Veröffentlichen", deleteReview: "Löschen", reviewSaved: "Bewertung gespeichert", noReviews: "Noch keine Bewertungen — sei die erste Person.", reviews_one: "{{count}} Bewertung", reviews_other: "{{count}} Bewertungen", reviews_zero: "Bewertungen", stats: { shared: "Geteilt", copies: "Wiederverwendungen", rating: "Bewertung", followers: "Follower" } },
  parentArea: { ...EN["parentArea"], linkStudent: "Benutzername der Schüler:in", send: "Anfrage senden", sent: "Anfrage gesendet", notFound: "Nicht gefunden", children: "Verknüpfte Schüler:innen", requests: "Eingehende Anfragen", accept: "Annehmen", decline: "Ablehnen", revoke: "Widerrufen", noRequests: "Keine Anfragen." },
  results: { ...EN["results"], title: "Klassenergebnisse", subtitle: "Abgaben, Noten und Durchschnitte deiner Aufgaben.", activities: "Aufgaben", submissions: "Abgaben", average: "Durchschnitt", exportCsv: "CSV exportieren", exportPdf: "Drucken / PDF", exported: "Datei heruntergeladen" },
  notify: { ...EN["notify"], title: "Klassen-Updates", hint: "Wie oft möchtest du Mitteilungen erhalten?", push: "Push bei neuen Mitteilungen", freq: { instant: "Sofort", daily: "Täglich", off: "Nie" } },
  audit: { ...EN["audit"], title: "Kommunikationsprotokoll", subtitle: "Wer Mitteilungen veröffentlicht, erhalten und geöffnet hat.", event: { announcement_created: "hat eine Mitteilung veröffentlicht", announcement_updated: "hat eine Mitteilung bearbeitet", announcement_deleted: "hat eine Mitteilung gelöscht", announcement_delivered: "hat eine Mitteilung erhalten", announcement_read: "hat eine Mitteilung geöffnet" } },
  studentHome: { ...EN["studentHome"], title: "Aufgaben aus deinen Klassen", sub_one: "{{count}} Aufgabe abzugeben", sub_other: "{{count}} Aufgaben abzugeben", all: "Alle Klassen ansehen", today: "Heute", noDue: "Ohne Frist", newsTitle: "Neues aus deinen Klassen", newsSub_one: "{{count}} Neuigkeit", newsSub_other: "{{count}} Neuigkeiten" },
  parent: { ...EN["parent"], title: "Elternbereich", subtitle: "Der Fortschritt deines Kindes – nur lesen und nur mit Zustimmung.", link: "Schüler:in verknüpfen", linkHint: "Die Schüler:in bestätigt die Verknüpfung im Profil.", pending: "Wartet auf Zustimmung", accepted: "Verknüpft", empty: "Keine verknüpften Schüler:innen." },
};

const pt: Record<string, unknown> = {
  ...EN,
  role: { ...EN["role"], question: "Como vais usar o Lumen?", subtitle: "Podes mudar quando quiseres no perfil.", student: "Estudante", studentDesc: "Estudo, tutor IA, mapas, quizzes e planeador.", teacher: "Professor(a)", teacherDesc: "Turmas virtuais, conteúdos e progresso.", parent: "Encarregado de educação", parentDesc: "Acompanha o progresso do teu educando, só leitura.", saved: "Perfil atualizado" },
  profile: { ...EN["profile"], title: "Perfil de docente", subtitle: "Só o essencial: o resto é opcional.", name: "Nome", subjects: "Disciplinas lecionadas", subjectsHint: "Separadas por vírgulas", levels: "Nível escolar", school: "Escola (opcional)", years: "Anos de experiência (opcional)", languages: "Idiomas", classTypes: "Tipos de aula", bio: "Sobre ti (opcional)", isPublic: "Visível para outros docentes", save: "Guardar perfil", saved: "Perfil guardado" },
  dash: { ...EN["dash"], title: "Área do docente", subtitle: "As tuas turmas e ferramentas.", classes: "As minhas turmas", create: "Conteúdos para criar", assigned: "Atividades atribuídas", results: "Resultados", contents: "Conteúdos", community: "Comunidade docente", assistant: "Assistente IA para docentes", empty: "Ainda não há turmas. Cria uma para começar." },
  classes: { ...EN["classes"], new: "Nova turma", newTitle: "Criar turma virtual", name: "Nome da turma", subject: "Disciplina", level: "Nível", year: "Ano letivo", description: "Descrição (opcional)", create: "Criar turma", created: "Turma criada", members: "{{count}} estudantes", code: "Código de acesso", copyCode: "Copiar código", copied: "Código copiado", copyLink: "Copiar link de convite", rotate: "Gerar novo código", rotated: "Novo código gerado", inviteOpen: "Inscrições abertas", leaderboard: "Classificação da turma (opcional)", archive: "Arquivar turma", open: "Abrir turma", settings: "Definições", studentsTitle: "Estudantes inscritos", studentsEmpty: "Ainda ninguém: partilha o código.", progressTitle: "Progresso dos estudantes", progressAvg: "Média", progressRefresh: "Atualizar" },
  join: { ...EN["join"], title: "Entrar numa turma", subtitle: "Insere o código dado pelo teu professor.", code: "Código da turma", submit: "Entrar", joined: "Entraste em {{name}}", notFound: "Código inválido", myClasses: "As minhas turmas", leave: "Sair da turma", left: "Saíste da turma" },
  board: { ...EN["board"], title: "Mural", write: "Escrever um aviso", publish: "Publicar", ask: "Perguntar ao professor", send: "Enviar pergunta", sent: "Pergunta enviada", answer: "Responder", answered: "Resposta do professor", empty: "Ainda sem mensagens.", question: "Pergunta", announcement: "Aviso", anonymousBadge: "Anónimo", delete: "Eliminar" },
  tabs: { ...EN["tabs"], board: "Mural", work: "Atividades", challenges: "Desafios", students: "Estudantes", settings: "Definições" },
  studio: { ...EN["studio"], title: "Criar conteúdo", subtitle: "Testes, exercícios e explicações num minuto.", kind: "Tipo de conteúdo", topic: "Tema", subject: "Disciplina", difficulty: "Dificuldade", easy: "Fácil", medium: "Média", hard: "Difícil", generate: "Gerar com IA", generating: "A preparar o conteúdo...", generated: "Conteúdo criado", library: "Os meus materiais", empty: "Ainda sem materiais.", assign: "Atribuir à turma", preview: "Pré-visualizar" },
  assign: { ...EN["assign"], title: "Atribuir à turma", chooseClass: "Turma", instructions: "Instruções para os estudantes", due: "Entregar até", maxScore: "Pontuação máxima", confirm: "Atribuir", done: "Atividade atribuída", list: "Atividades da turma", empty: "Sem atividades atribuídas." },
  work: { ...EN["work"], title: "Trabalhos e testes", subtitle: "Atividades atribuídas pelos teus professores.", empty: "Não tens atividades atribuídas.", submit: "Entregar", submitted: "Entregue", sent: "Entrega enviada", feedback: "Feedback do professor", pending: "A aguardar correção" },
  grade: { ...EN["grade"], title: "Entregas", empty: "Ainda sem entregas.", score: "Nota", feedback: "Feedback", save: "Guardar nota", saved: "Nota guardada", analyze: "Análise IA dos resultados" },
  challenge: { ...EN["challenge"], title: "Desafios da turma", empty: "Sem desafios ativos.", new: "Novo desafio", create: "Criar desafio", created: "Desafio criado", leaderboard: "Classificação", target: "Objetivo", reward: "Recompensa XP" },
  share: { ...EN["share"], title: "Comunidade docente", subtitle: "Encontra materiais partilhados por colegas e reutiliza-os.", search: "Procurar por título ou tema", copy: "Copiar para a minha biblioteca", copied: "Material copiado" , sorts: { recent: "Recentes", popular: "Populares", rated: "Melhor avaliados" }, onlySaved: "Guardados", onlyFollowing: "Que sigo", save: "Guardar", saved: "Guardado", follow: "Seguir", following: "A seguir", rateIt: "Avalia este material", yourReview: "A tua avaliação", reviewPh: "Como correu na aula? O que mudarias?", sendReview: "Publicar", deleteReview: "Eliminar", reviewSaved: "Avaliação guardada", noReviews: "Ainda sem avaliações — sê o primeiro.", reviews_one: "{{count}} avaliação", reviews_other: "{{count}} avaliações", reviews_zero: "Avaliações", stats: { shared: "Partilhados", copies: "Reutilizações", rating: "Media", followers: "Seguidores" } },
  parentArea: { ...EN["parentArea"], linkStudent: "Utilizador do estudante", send: "Enviar pedido", sent: "Pedido enviado", notFound: "Estudante não encontrado", children: "Estudantes ligados", requests: "Pedidos recebidos", accept: "Aceitar", decline: "Recusar", revoke: "Revogar", noRequests: "Sem pedidos." },
  results: { ...EN["results"], title: "Resultados da turma", subtitle: "Entregas, notas e médias das atividades.", activities: "Atividades", submissions: "Entregas", average: "Média", exportCsv: "Exportar CSV", exportPdf: "Imprimir / PDF", exported: "Ficheiro descarregado" },
  notify: { ...EN["notify"], title: "Novidades da turma", hint: "Escolhe com que frequência recebes os avisos.", push: "Notificação de novos avisos", freq: { instant: "Imediato", daily: "Diário", off: "Nunca" } },
  audit: { ...EN["audit"], title: "Registo de comunicações", subtitle: "Quem publicou, recebeu e abriu os avisos.", event: { announcement_created: "publicou um aviso", announcement_updated: "editou um aviso", announcement_deleted: "eliminou um aviso", announcement_delivered: "recebeu um aviso", announcement_read: "abriu um aviso" } },
  studentHome: { ...EN["studentHome"], title: "Trabalhos das tuas turmas", sub_one: "{{count}} trabalho por entregar", sub_other: "{{count}} trabalhos por entregar", all: "Ver todas as turmas", today: "Hoje", noDue: "Sem prazo", newsTitle: "Novidades das tuas turmas", newsSub_one: "{{count}} novidade", newsSub_other: "{{count}} novidades" },
  parent: { ...EN["parent"], title: "Área da família", subtitle: "O progresso do teu educando, só leitura e com consentimento.", link: "Ligar um estudante", linkHint: "O estudante aprova a ligação no perfil.", pending: "A aguardar consentimento", accepted: "Ligado", empty: "Sem estudantes ligados." },
};

export const teacher: LangMap = { ...base, es, fr, de, pt };
